import {
  ActiveFilter,
  ActiveFilterPills,
} from "@/shared/components/common/ActiveFilterPills";
import {
  EntityListFilter,
  EntityListHeaderShell,
} from "@/features/tenantUsers/components";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getTeachersListFilterSections } from "../constants/filterConfig";
import { getTeachersListingConfig } from "../constants/listingConfig";
import { initialTeacherListFilters, useTeachersListStore } from "@/store";
import { useInstitutionOptions } from "../hooks/useInstitutionOptions";
import { TeacherInstituteFilter } from "./TeacherInstituteFilter";

export function TeachersHeader() {
  const { t } = useTranslation(["teachers", "common"]);
  const listingConfig = getTeachersListingConfig(t);
  const listFilterValues = useTeachersListStore((s) => s.listFilterValues);
  const setListFilterValues = useTeachersListStore(
    (s) => s.setListFilterValues,
  );
  const { setPage: setCurrentPage } = useTeachersListStore();
  const searchQuery = useTeachersListStore((s) => s.searchQuery);
  const setSearchQuery = useTeachersListStore((s) => s.setSearchQuery);

  const filterSections = useMemo(() => getTeachersListFilterSections(t), [t]);

  const instituteId =
    typeof listFilterValues.institute_id === "string"
      ? listFilterValues.institute_id.trim()
      : "";
  const { data: instituteOptions } = useInstitutionOptions();

  const activeFilters: ActiveFilter[] = useMemo(() => {
    const a: ActiveFilter[] = [];
    for (const key of ["board", "class", "section"] as const) {
      const value = listFilterValues[key];
      if (typeof value === "string" && value.trim()) {
        a.push({
          id: key,
          label: t(`teachers:activeFilter.${key}`),
          displayText: value.trim(),
        });
      }
    }
    if (instituteId) {
      a.push({
        id: "institute_id",
        label: t("teachers:activeFilter.institute"),
        displayText:
          instituteOptions?.find((o) => o.value === instituteId)?.label ??
          instituteId,
      });
    }
    const accountStatus =
      typeof listFilterValues.accountStatus === "string"
        ? listFilterValues.accountStatus
        : "";
    if (accountStatus) {
      a.push({
        id: "accountStatus",
        label: t("teachers:activeFilter.status"),
        displayText: accountStatus,
      });
    }
    return a;
  }, [listFilterValues, instituteId, instituteOptions, t]);

  const handleInstituteChange = (id: string) => {
    setListFilterValues({ ...listFilterValues, institute_id: id });
    setCurrentPage(1);
  };

  const handleRemoveFilter = (id: string) => {
    setListFilterValues({ ...listFilterValues, [id]: "" });
    setCurrentPage(1);
  };

  const handleClearAllFilters = () => {
    setListFilterValues({ ...initialTeacherListFilters });
    setSearchQuery("");
    setCurrentPage(1);
  };

  return (
    <EntityListHeaderShell
      searchPlaceholder={listingConfig.searchPlaceholder}
      searchValue={searchQuery}
      onSearchChange={setSearchQuery}
      sampleSheetPath={listingConfig.sampleSheetPath}
      bulkUploadPath={listingConfig.bulkUploadPath}
      addPath={listingConfig.addPath}
      addLabel={listingConfig.addLabel}
      filterControl={
        <>
          <TeacherInstituteFilter
            value={instituteId}
            onChange={handleInstituteChange}
          />
          <EntityListFilter
            sections={filterSections}
            values={listFilterValues}
            onChange={(next) =>
              // The popover only knows its own sections; keep the institute selection.
              setListFilterValues({ ...next, institute_id: instituteId })
            }
            onApply={() => setCurrentPage(1)}
            activeFilters={activeFilters}
            onRemoveFilter={handleRemoveFilter}
            onClearAllFilters={handleClearAllFilters}
            showPills={false}
          />
        </>
      }
      footerContent={
        <ActiveFilterPills
          filters={activeFilters}
          onRemove={handleRemoveFilter}
          onClearAll={handleClearAllFilters}
        />
      }
    />
  );
}